// Timer ausserhalb des Kochmodus (W8, Astrid: "laufen weiter, wenn man den Kochmodus verlaesst").
// Ueberall in der App: Erinnerungen als Toast mit Gong. Ausserhalb des Kochmodus: oben eine kleine Leiste mit dem
// naechsten Timer ("Köcheln 12:31", Tipp = zurueck zum Kochen) und beim Klingeln ein Sheet mit "Ton aus".
import { useEffect, useId, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Icon, useToast } from '../ui';
import { dauerSprache, uhrText } from './format';
import { restMs, uhr, useJetzt, useTimer } from './uhr';
import s from './waechter.module.css';

function labelKurz(label: string): string {
  return label.replace(/, \d\. Seite$/, '').replace(/ \d+$/, '');
}

export function TimerWaechter() {
  const ort = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const timer = useTimer();
  const imKochmodus = /^\/rezept\/[^/]+\/kochen$/.test(ort.pathname);
  const anmelden = ort.pathname === '/anmelden';
  const aktiv = timer.filter((t) => t.status === 'laeuft' || t.status === 'pausiert' || t.status === 'klingelt');
  const jetzt = useJetzt(!imKochmodus && aktiv.some((t) => t.status === 'laeuft'));
  const titelId = useId();
  const knopfRef = useRef<HTMLButtonElement>(null);

  useEffect(
    () =>
      uhr.onEreignis((e) => {
        if (e.art === 'erinnerung') toast(`Jetzt: ${e.text}`);
      }),
    [toast],
  );

  // Lyra W9 A6 / QA 22: Auf Tab-Seiten steht die Pille oben und schiebt den Inhalt nach unten (nichts verdeckt).
  // Auf Seiten mit Kopf-Knoepfen (Rezept, Nora, Import) steht sie unter der Knopfreihe, damit Teilen treffbar bleibt.
  const tabSeite = /^\/(entdecken|rezepte|woche|einkaufen|profil)?(\/|$)/.test(ort.pathname) && !ort.pathname.startsWith('/rezept/');
  const pilleSichtbar = !imKochmodus && !anmelden && aktiv.length > 0;
  useEffect(() => {
    const html = document.documentElement;
    if (pilleSichtbar && tabSeite) html.dataset.timerPille = '1';
    else delete html.dataset.timerPille;
    return () => {
      delete html.dataset.timerPille;
    };
  }, [pilleSichtbar, tabSeite]);

  const klingelt = timer.filter((t) => t.status === 'klingelt').sort((a, b) => (a.klingeltSeit ?? 0) - (b.klingeltSeit ?? 0))[0];
  useEffect(() => {
    if (klingelt && !imKochmodus) knopfRef.current?.focus();
  }, [klingelt?.id, imKochmodus]);

  if (imKochmodus || anmelden || aktiv.length === 0) return null;
  const naechster = [...aktiv].sort((a, b) => restMs(a, jetzt) - restMs(b, jetzt))[0];
  if (!naechster) return null;
  const ziel = `/rezept/${naechster.rezeptSlug}/kochen?weiter=1`;

  return (
    <>
      <Link
        to={ziel}
        className={`${s.pille} ${tabSeite ? '' : s.pilleUnterKopf}`}
        aria-label={`${labelKurz(naechster.label)}, noch ${dauerSprache(restMs(naechster, jetzt))}. Zurück zum Kochen: ${naechster.rezeptName}`}
      >
        <Icon name="stoppuhr" groesse={18} strich={1.9} />
        <span>{labelKurz(naechster.label)}</span>
        <b>{uhrText(restMs(naechster, jetzt))}</b>
        {aktiv.length > 1 && <span className={s.mehr}>+{aktiv.length - 1}</span>}
        <Icon name="chevronRechts" groesse={16} strich={2} />
      </Link>
      {klingelt && (
        <div className={s.ebene}>
          <div className={s.scrim} aria-hidden="true" />
          <div role="alertdialog" aria-modal="true" aria-labelledby={titelId} className={s.sheet}>
            <span className={s.griff} aria-hidden="true" />
            <div className={s.kreis} aria-hidden="true">
              <Icon name="glockeKlingelt" groesse={26} strich={2} />
            </div>
            <h2 id={titelId} className={s.titel}>
              {labelKurz(klingelt.label)} ist fertig
            </h2>
            <p className={s.text}>
              {klingelt.rezeptName}
              {klingelt.schritt ? `, Schritt ${klingelt.schritt}` : ''}
              {klingelt.bedingung ? `: ${klingelt.bedingung}.` : '.'}
            </p>
            <button ref={knopfRef} type="button" className={s.primaer} onClick={() => uhr.tonAus(klingelt.id)}>
              Ton aus
            </button>
            <button
              type="button"
              className={s.kontur}
              onClick={() => {
                uhr.tonAus(klingelt.id);
                navigate(`/rezept/${klingelt.rezeptSlug}/kochen?weiter=1`);
              }}
            >
              Zum Kochmodus
            </button>
          </div>
        </div>
      )}
    </>
  );
}
